import type { HTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import styles from "./EmptyState.module.css";

export type EmptyStateProps = Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
  /** Ce qui n'est pas là, dit simplement : « Aucune écriture ». */
  title: ReactNode;
  /** Pourquoi, et quoi faire ensuite. */
  description?: ReactNode;
  /** Pictogramme ou emoji. Décoratif. */
  icon?: ReactNode;
  /** L'action qui sort de l'état vide — souvent un `Button`. */
  action?: ReactNode;
  /** Version resserrée, pour un état vide à l'intérieur d'une carte. */
  compact?: boolean;
};

/**
 * Il n'y a rien à montrer — et ce n'est pas une erreur.
 *
 * Un portefeuille neuf, un catalogue vide, une dernière page d'historique sont
 * des états normaux. Les distinguer d'une panne est la moitié du travail :
 * l'autre moitié est de proposer l'action qui en sort.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  compact = false,
  className,
  ...rest
}: EmptyStateProps) {
  return (
    <div className={cx(styles.vide, compact && styles.compact, className)} {...rest}>
      {icon !== undefined && (
        <span className={styles.icone} aria-hidden="true">
          {icon}
        </span>
      )}
      <p className={styles.titre}>{title}</p>
      {description !== undefined && (
        <p className={styles.description}>{description}</p>
      )}
      {action !== undefined && <div className={styles.action}>{action}</div>}
    </div>
  );
}
